import React, { useEffect, useMemo, useState } from "react";
import { Dispatch, SetStateAction } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import {
  Modal,
  Button,
  Select,
  MenuItem,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAddNotificationsMutation } from "../services/notification";
import { useGetUsersQuery } from "../services/user";
import type { AdminUser } from "../types/General";
import { ADMIN_NOTIFICATION_TYPE } from "../utils/enums";
import { showError, showToast } from "../constants/toasts";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  getNotificationList: () => void;
}

export default function AddNotification({
  open,
  onClose,
  setOpen,
  getNotificationList,
}: ModalProps) {
  const [autoCompleteOpen, setAutoCompleteOpen] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const { data: usersRes } = useGetUsersQuery({
    start: 1,
    length: 1000,
    search: "",
  });
  const [addNotification, { isLoading }] = useAddNotificationsMutation();

  const users: AdminUser[] = useMemo(
    () => (usersRes?.data?.response_object || []) as AdminUser[],
    [usersRes]
  );

  const userOptions = useMemo(() => {
    const base = users.map((u) => ({
      id: String(u.id),
      label: u.email || u.phone_number || `User ${u.id}`,
    }));
    return [{ id: "all", label: "Select All Users" }, ...base];
  }, [users]);

  const handleUsersChange = (
    event: any,
    value: Array<{ id: string; label: string }>
  ) => {
    const isSelectAllClicked = value.some((user) => user.id === "all");
    if (isSelectAllClicked) {
      const isAllSelected = selectedUsers.length === userOptions.length - 1;
      if (isAllSelected) {
        setSelectedUsers([]);
      } else {
        const allUsers = userOptions.filter((user) => user.id !== "all");
        setSelectedUsers(allUsers);
      }
    } else {
      setSelectedUsers(value);
    }
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      message: "",
      notification_type: ADMIN_NOTIFICATION_TYPE.BROADCAST,
    },
    validationSchema: Yup.object({
      title: Yup.string().trim().required("Title is required"),
      message: Yup.string().trim().required("Message is required"),
      notification_type: Yup.number()
        .oneOf([1, 2])
        .required("Type is required"),
    }),
    onSubmit: async (values) => {
      try {
        const selectedIds = selectedUsers
          .filter((u) => u.id !== "all")
          .map((u) => u.id);
        const usersCsv = selectedIds.join(",");
        const notification_for =
          selectedUsers.length === userOptions.length - 1 ? 1 : 2;
        const body = {
          title: values.title,
          message: values.message,
          notification_type: values.notification_type,
          notification_for,
          users: usersCsv,
        };
        await addNotification({ body }).unwrap();
        showToast("Notification added successfully");
        getNotificationList();
        formik.resetForm();
        setSelectedUsers([]);
        setOpen(false);
      } catch (e: any) {
        showError(e?.data?.message || "Failed to add notification");
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setSelectedUsers([]);
    setOpen(false);
    onClose();
  };

  return (
    <Modal
      className="modal addNotification_modal"
      open={open}
      onClose={handleClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>Add Notification</h2>
            <div className="btn-close" onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_100">
                <TextField
                  variant="outlined"
                  placeholder="Notification Title"
                  fullWidth
                  hiddenLabel
                  name="title"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.title && Boolean(formik.errors.title)}
                  helperText={formik.touched.title && formik.errors.title}
                />
              </div>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={userOptions}
                value={selectedUsers}
                onChange={handleUsersChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onOpen={() => setAutoCompleteOpen(true)}
                onClose={() => setAutoCompleteOpen(false)}
                renderOption={(props, option, { selected }) => {
                  const isAllOption = option.id === "all";
                  const isAllSelected =
                    selectedUsers.length === userOptions.length - 1; // exclude "Select All"

                  return (
                    <li {...props}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isAllOption ? isAllSelected : selected}
                            indeterminate={
                              isAllOption &&
                              selectedUsers.length > 0 &&
                              !isAllSelected
                            }
                          />
                        }
                        name="option"
                        label={option.label}
                      />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Users"
                    variant="outlined"
                  />
                )}
              />
              <div className="control_group w_50">
                <Select
                  fullWidth
                  value={formik.values.notification_type}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "notification_type",
                      Number(e.target.value)
                    )
                  }
                >
                  <MenuItem disabled value={0}>
                    Select Type
                  </MenuItem>
                  <MenuItem value={1}>Email</MenuItem>
                  <MenuItem value={2}>Broadcast</MenuItem>
                </Select>
              </div>
              {/* Template dropdown intentionally disabled per requirement */}
              <div className="control_group w_100">
                <TextField
                  variant="outlined"
                  placeholder="Notification Message"
                  fullWidth
                  hiddenLabel
                  multiline
                  maxRows={5}
                  minRows={5}
                  name="message"
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.message && Boolean(formik.errors.message)
                  }
                  helperText={formik.touched.message && formik.errors.message}
                />
              </div>
            </div>
            <div className="footer_btn_flex">
              <Button className="btn btn_grey" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="btn btn_primary"
                type="submit"
                disabled={isLoading}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
